import { useEffect, useId, useState, type FormEvent } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import type { Daily } from '@/api/types';
import { endpoints } from '@/api';
import { ApiError, errorMessage } from '@/api/errors';
import { qk, useDaily } from '@/api/queries';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Field';
import { cn } from '@/lib/cn';

export const DAILY_MAX = 140;
const LINK_RE = /(https?:\/\/|www\.)\S+|\b[a-z0-9-]+\.(com|net|org|io|co|me|app|gg|ly|tv|link|xyz)\b/i;
const HIDE_KEY = 'popin.daily.hidden';

/** SPEC_V3 §5: trimmed 1..140, no links, no control characters. Returns the clean text or an error. */
export function validateDaily(raw: string): { text: string; error: string | null } {
  // eslint-disable-next-line no-control-regex
  const text = raw.replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return { text, error: 'Write a few words first.' };
  if (text.length > DAILY_MAX) return { text, error: `Keep it under ${DAILY_MAX} characters.` };
  if (LINK_RE.test(text)) return { text, error: 'No links here, just your answer.' };
  return { text, error: null };
}

export function dailyErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.details.reason === 'links') return 'No links here, just your answer.';
    if (e.code === 'rate_limited' || e.details.reason === 'daily_limit') return 'You’ve changed this a lot today. It’ll keep until tomorrow.';
    if (e.code === 'validation_failed') return e.message || 'Answers are 1–140 characters, no links.';
  }
  return errorMessage(e, 'Couldn’t save. Try again.');
}

function readHidden(): string | null {
  try {
    return localStorage.getItem(HIDE_KEY);
  } catch {
    return null;
  }
}

/** The answer form + saved state. Used on Home and in Ideas → Today. */
export function DailyAnswer({ daily, onSaved, compact }: { daily: Daily; onSaved?: (d: Daily) => void; compact?: boolean }) {
  const qc = useQueryClient();
  const id = useId();
  const mine = daily.my_answer;
  const [editing, setEditing] = useState(!mine);
  const [text, setText] = useState(mine?.text ?? '');
  const [share, setShare] = useState(mine?.share ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const v = validateDaily(text);
  const typedLink = !!text && LINK_RE.test(text);

  useEffect(() => {
    if (!mine) setEditing(true);
  }, [mine]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (v.error) return setErr(v.error);
    setBusy(true);
    setErr(null);
    try {
      const d = await endpoints.answerDaily(v.text, share);
      qc.setQueryData(qk.daily, d);
      setEditing(false);
      onSaved?.(d);
    } catch (e2) {
      setErr(dailyErrorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  if (mine && !editing) {
    return (
      <div data-testid="daily-answered">
        <p className="text-[15px] leading-snug">
          <span className="text-muted">You said </span>“{mine.text}”
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[13px] text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Icon name={mine.share ? 'people' : 'shield'} size={16} />
            {mine.share ? 'Shared with people you meet today' : 'Just for you'}
          </span>
          <button
            type="button"
            onClick={() => {
              setText(mine.text);
              setShare(mine.share);
              setEditing(true);
            }}
            className="-my-2 h-10 rounded-full px-1 font-semibold text-fg underline decoration-line underline-offset-4 hover:decoration-fg"
            data-testid="daily-edit"
          >
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={save} className="space-y-3" data-testid="daily-form">
      <div>
        <label htmlFor={id} className="sr-only">
          Your answer
        </label>
        <div className={cn('flex items-center gap-2 rounded-[16px] border bg-ground pr-1.5 focus-within:border-fg', err || typedLink ? 'border-danger' : 'border-line')}>
          <input
            id={id}
            value={text}
            maxLength={DAILY_MAX + 20}
            onChange={(e) => {
              setText(e.target.value);
              if (err) setErr(null);
            }}
            placeholder="A few words is plenty"
            autoComplete="off"
            className="h-12 min-w-0 flex-1 bg-transparent px-3.5 text-[16px] placeholder:text-muted focus:outline-none"
            data-testid="daily-input"
          />
          {text.length > DAILY_MAX - 30 && <span className={cn('text-[13px] tabular-nums', text.length > DAILY_MAX ? 'text-danger' : 'text-muted')}>{DAILY_MAX - text.length}</span>}
          <Button type="submit" size="sm" accent="pop" loading={busy} disabled={!text.trim() || !!v.error} data-testid="daily-save">
            {mine ? 'Save' : 'Answer'}
          </Button>
        </div>
        {(err || typedLink) && (
          <p role="alert" className="mt-1.5 text-sm font-medium text-danger">
            {err ?? 'No links here, just your answer.'}
          </p>
        )}
      </div>
      <Switch label="Share with people I meet today" description={compact ? undefined : 'They see it only if they answered too.'} checked={share} onChange={setShare} testId="daily-share" />
      {mine && (
        <button type="button" onClick={() => setEditing(false)} className="h-10 text-sm font-semibold text-muted hover:text-fg">
          Cancel
        </button>
      )}
    </form>
  );
}

/** Home: today's question. Optional, dismissable for the day, never nags. */
export function DailyCard() {
  const daily = useDaily();
  const reduced = useReducedMotion();
  const [hidden, setHidden] = useState(() => readHidden());
  const date = daily.data?.date ?? null;
  if (date && hidden === date) return null;

  const hide = () => {
    if (!date) return;
    try {
      localStorage.setItem(HIDE_KEY, date);
    } catch {
      /* private mode: hide for this visit only */
    }
    setHidden(date);
  };

  return (
    <motion.section
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.3, ease: 'easeOut', delay: 0.1 }}
      aria-labelledby="daily-heading"
      data-testid="daily-card"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 id="daily-heading" className="text-[17px] font-semibold">
          Today’s question
        </h2>
        {date && (
          <button type="button" onClick={hide} aria-label="Hide for today" className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg" data-testid="daily-hide">
            <Icon name="x" size={18} />
          </button>
        )}
      </div>
      <div className="rounded-card bg-surface p-5 sm:p-6">
        {daily.isLoading ? (
          <div className="space-y-3" role="status" aria-label="Loading today’s question">
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : daily.isError || !daily.data ? (
          <div className="flex flex-wrap items-center gap-3" data-testid="daily-error">
            <p className="text-[15px] text-muted">Couldn’t load today’s question.</p>
            <Button size="sm" variant="subtle" accent="paper" icon="flip" onClick={() => daily.refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          <>
            <p id="daily-title" className="text-[22px] font-semibold leading-tight tracking-[-0.01em]" data-testid="daily-question">
              {daily.data.question.text}
            </p>
            <p className="mb-4 mt-1.5 text-[15px] text-muted">
              {daily.data.my_answer ? 'Meet someone who answered too and you’ll both see each other’s in Ideas.' : 'Answer to see theirs. Meet someone who answered too and you both see each other’s. Totally optional.'}
            </p>
            <DailyAnswer daily={daily.data} />
          </>
        )}
      </div>
    </motion.section>
  );
}
